import { type ReactNode, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { MODULES } from "../lib/modules.ts";
import { useRole } from "../lib/role.tsx";
import { useAdminNotificationCount } from "../lib/useAdminNotifications.ts";
import { NotificationsPanel, type NotificationOrderRef } from "./NotificationsPanel.tsx";
import { OrderDrawer } from "../modules/orders/OrderDrawer.tsx";
import { Sidebar } from "./Sidebar.tsx";
import { Topbar } from "./Topbar.tsx";

interface AdminLayoutProps {
  children?: ReactNode;
  userInfo?: {
    name: string;
    role: string;
    city?: string;
  };
}

function normalizePath(pathname: string): string {
  if (pathname === "/") return "";
  return pathname.replace(/^\/m\/?/, "").replace(/^\/+|\/+$/g, "");
}

function findCurrentModule(pathname: string) {
  const current = normalizePath(pathname);
  return [...MODULES]
    .sort((left, right) => right.slug.length - left.slug.length)
    .find((module) => {
      if (module.slug === "") return current === "";
      return current === module.slug || current.startsWith(`${module.slug}/`);
    });
}

export function AdminLayout({ children, userInfo }: AdminLayoutProps) {
  const role = useRole();
  const location = useLocation();
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [openOrder, setOpenOrder] = useState<NotificationOrderRef | null>(null);
  const notificationCount = useAdminNotificationCount();
  const currentModule = useMemo(() => findCurrentModule(location.pathname), [location.pathname]);
  const effectiveUserInfo = userInfo ?? {
    name: "Оператор",
    role,
  };

  return (
    <div className="admin-shell" data-testid="admin-layout">
      <aside>
        <Sidebar current={currentModule?.slug ?? ""} counts={{}} userInfo={effectiveUserInfo} />
      </aside>
      <div className="admin-main">
        <Topbar
          title={currentModule?.label ?? "Рабочий стол"}
          notifyCount={notificationCount.data?.unread ?? 0}
          onBell={() => setNotifyOpen(true)}
        />
        <main className="admin-content">{children ?? <Outlet />}</main>
      </div>
      {notifyOpen && (
        <NotificationsPanel
          open={notifyOpen}
          onClose={() => setNotifyOpen(false)}
          onOpenOrder={(ref) => {
            setOpenOrder(ref);
            setNotifyOpen(false);
          }}
        />
      )}
      {openOrder && (
        <OrderDrawer
          id={openOrder.id}
          type={openOrder.type}
          onClose={() => setOpenOrder(null)}
        />
      )}
    </div>
  );
}
